import React, { FunctionComponent, useCallback, useEffect } from 'react';
import { EmailCheckFormFieldsModel } from '../../../models/EmailCheckFormFieldsModel';
import { EmailCheckResponseModel } from '../../../models';

// Declare the PexipEngage plugin on the window object
declare global {
  interface Window {
    PexipEngage: {
      Plugin: (el: any, config?: any) => void;
    },
    gaGlobal: any,
    dataLayer: any
  }
}


interface PexipPluginProps {
  formData: EmailCheckFormFieldsModel | null
  emailCheckResponse: EmailCheckResponseModel | null
}
export const PexipPlugin: FunctionComponent<PexipPluginProps> = ( { formData, emailCheckResponse }) => {


  // Since the global is installed async, when our code runs, it might not be available yet.
  const getEngagePlugin = () => {
    return new Promise((resolve) => {
      if (window.PexipEngage?.Plugin) resolve(window.PexipEngage?.Plugin);

      // if not available, listen for any event, global will be available then.
      function listener() {
        resolve(window.PexipEngage?.Plugin);
        document.removeEventListener("SkedifyPluginEvent", listener);
      }

      document.addEventListener("SkedifyPluginEvent", listener);
    });
  }

// use the `awaitFirstInstance` API to get a Promise with the first available instance.
  const getPluginInstance = useCallback(() => {
    return getEngagePlugin().then((plugin: any) => plugin.awaitFirstInstance());
  }, []);

  const getExternalId = () => {
    try {
      // Attempt to access window.gaGlobal.vid
      const visitorID = window.gaGlobal.vid;

      // Check if the visitorID is not undefined
      if (visitorID !== undefined) {
        return visitorID;
      } else {
        // Return null if window.gaGlobal.vid is undefined
        return null;
      }
    } catch (error) {
      // Catch any errors (e.g., window.gaGlobal is undefined) and return null
      console.error("Error accessing window.gaGlobal.vid:", error);
      return null;
    }
  }

  useEffect(() => {
    if (!formData) return;
    getPluginInstance().then((instance: any) => {
      // interact with the PexipEngagePlugin here.
      instance.appointment.meta.set("dossier-id", emailCheckResponse?.dossierId);
      instance.appointment.meta.set("externalreference", getExternalId());

      // set GTM etc
      // We use the global constant definition to map to our own identifiers for GTM, as the global constants of Plugin.X are not guaranteed to stay the same.
      const EVENT_TYPES = {
        [instance.EVENT_LOADED]: 'plugin-loaded',
        [instance.EVENT_APPOINTMENT_CREATED]: 'appointment-created',
        [instance.EVENT_APPOINTMENT_EDITED]: 'appointment-edited',
        [instance.EVENT_APPOINTMENT_CANCELLED]: 'appointment-cancelled',
        [instance.EVENT_APPOINTMENT_COMPLETED]: 'appointment-completed',
        [instance.EVENT_INVITE_ACCEPTED]: 'invite-accepted',
        [instance.EVENT_STEP_SHOWN]: 'user-progress',
      };

      instance.addEventListener((event: any) => {
        //console.log('processing event:', event.detail);

        /**
         * Check if the received event is one we wish to track.
         * To ignore certain events, simply remove them from the EVENT_TYPES object.
         */
        if (event.detail.type in EVENT_TYPES) {
          // Get the GTM_KEY we wish to use in our own tracking.
          const GTM_KEY = EVENT_TYPES[event.detail.type];

          //if (GTM_KEY !== 'user-progress') {
          let gtm = {event: GTM_KEY, activeStep: null, currentSelection: null};
          if (event.detail?.payload) {
            const {step, ...currentSelection} = event.detail.payload;
            gtm = {
              event: GTM_KEY,
              activeStep: step,
              currentSelection,
            }
          }
          window.dataLayer.push(gtm);
          //}
        } else {
          // In this example, we add this warning here for clarity, to see which events we're ignoring.
          //console.warn('Ignoring event: ', event.detail);

          return null;
        }
      });
    });


    const pluginElement = document.querySelector('#skedify-plugin-wrapper')
    if (!pluginElement) return;
    pluginElement.setAttribute('data-pexip-office-ids', formData.office.intermediaryCode)
    pluginElement.setAttribute('data-pexip-customer-first_name', formData.name)
    pluginElement.setAttribute('data-pexip-customer-email', formData.email)
    pluginElement.setAttribute('data-pexip-customer-last_name', (formData.tussenvoegsel) ? `${formData.tussenvoegsel} ${formData.lastname}` : formData.lastname)
    pluginElement.setAttribute('data-pexip-customer-phone_number', formData.phoneNumber)

    window.PexipEngage.Plugin(document.querySelector('#skedify-plugin-wrapper'));
  }, [formData, emailCheckResponse, getPluginInstance]);

  return (<>
    <div
      id="skedify-plugin-wrapper"
      data-pexip-version="1.0.0"
      data-pexip-flow="SMTCQ"
      data-pexip-language="nl"
      data-pexip-customer-time_zone="Europe/Amsterdam"
      data-pexip-office-type="externalId"
    ></div>

  </>);
}
